import _ from 'lodash';
import environment from '../environment';
import { pinBuilder } from '../core/map/pinBuilder';
import Promise from "bluebird";
import templates from '../core/templates';
import Leaflet from './Leaflet';
import ColorBrewer from '../core/tools/colorbrewer';
import { FuelStationParameter } from '../../constants/fuelStationParameter';
import { LayerContextEnum } from '../../enuns/layerContext.enum';
import translate from '../core/tools/translate';

class FuelStationLayer {
    constructor (currentMap) {
        const clusterImageFuel = new Image();
        clusterImageFuel.src = `${environment.get('IMAGES_HOST')}/icon_marker_gas_station.png`;

        const onClick = (feature, layer) => {};

        const clusterTemplate = cluster => {
            const data = {
                value: cluster.getAllChildMarkers().length,
            };

            return pinBuilder.create.pin.imageWithValue(data, clusterImageFuel);
        };

        const createPopupTemplate = (properties, layer) =>
            new Promise((resolve, reject) => {
                const fantasyName = properties.Parameters.filter(y => y.Key === FuelStationParameter.FantasyName || y.Key === FuelStationParameter.StationName);
                const socialReason = properties.Parameters.filter(y => y.Key === FuelStationParameter.CompanyName);

                const fn = fantasyName.length > 0 ? fantasyName[0].Value : null;
                const sr = socialReason.length > 0 ? socialReason[0].Value : null;

                const title: string = `${fn || sr}`;

                properties.IsEletricStation = properties.LayerContext === LayerContextEnum.EletricStation;

                properties.ParameterTemplates = properties.Parameters
                    .filter(x => x.Key !== FuelStationParameter.FantasyName
                        && x.Key !== FuelStationParameter.CompanyName
                        && x.Key !== FuelStationParameter.StationName)
                    .map(x => {
                        if (x.Translate && x.Translate.indexOf('Common.') === -1) {
                            return {...x, Translate: 'Common.' + x.Key};
                        }

                        x.Translate = 'Common.' + x.Key;

                        return x;
                    });

                const $template = $(templates('map-layer-fuel-station-popup', {title, ...properties}));

                resolve($template);
            });

        const getTitle = (parameters) => {
            const fantasyName = parameters.filter(y => y.Key === FuelStationParameter.FantasyName);
            const socialReason = parameters
                .filter(y => y.Key === FuelStationParameter.CompanyName || y.Key === FuelStationParameter.StationName)
                .filter(y => y.Value !== '');

            const fn = fantasyName.length > 0 ? fantasyName[0].Value : null;
            const sr = socialReason.length > 0 ? socialReason[0].Value : null;

            return `${fn || sr}`;
        };

        const createClusterPopupTemplate = features => {
            const properties = {
                fuelStations: _(features)
                    .filter(x => x.feature.properties.LayerContext === LayerContextEnum.FuelStation)
                    .map(x => ({...x, socialReason: getTitle(x.feature.properties.Parameters)}))
                    .uniqBy('socialReason'),
                eletricStations: _(features)
                    .filter(x => x.feature.properties.LayerContext === LayerContextEnum.EletricStation)
                    .map(x => ({...x, socialReason: getTitle(x.feature.properties.Parameters)}))
                    .uniqBy('socialReason'),
            };

            properties.fuelStationTotal = properties.fuelStations.size();
            properties.eletricStationTotal = properties.eletricStations.size();

            properties.fuelStations = properties.fuelStations.slice(0, 5);
            properties.eletricStations = properties.eletricStations.slice(0, 5);

            properties.wrapFuelStation = properties.fuelStations.size() === 5;
            properties.wrapEletricStations = properties.eletricStations.size() === 5;

            properties.hasEletricStation = properties.eletricStations.size() > 0;
            properties.hasFuelStation = properties.fuelStations.size() > 0;

            return new Promise((resolve, reject) => {
                const template = $(templates('map-layer-fuel-station-cluster-popup', properties));
                resolve(template);
            });
        };

        return new Leaflet.D3Layer({
            map: currentMap,
            id: `spatial_fuel_station_layer`,
            remote: {
                method: 'POST',
                url: `${environment.get('API_HOST')}/Layer/GetFuelStations`,
                params: { }
            },
            scaleRange: [7, 20],
            showAll: false,
            autofocus: false,
            uniqueField: 'Id',
            sort: 'SocialName',
            click: onClick,
            legend: {
                title: translate('Common.FuelStation'),
                text: (data) => FuelStationLayer.getLegendName[data.LayerContext](),
                icon: (data) => FuelStationLayer.getPinIcon[`${data.LayerContext}_xs`]
            },
            popup: {
                template: createPopupTemplate,
                clusterTemplate: createClusterPopupTemplate,
                trigger: 'mouseover',
                options: {
                    offset: L.point(0, -5),
                    closeButton: false,
                    autoclose: true
                },
                single: true
            },
            type: 'cluster',
            symbology: {
                type: 'polymorph',
                cluster: {
                    disableAtZoom: 20,
                    template: clusterTemplate,
                },
                heat: {
                    theme: ColorBrewer.getHeatColors(ColorBrewer.RED_YELLOW_GREEN)
                },
                style: {
                    icon: {
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_fuel_station.png`,
                        getUrl: (data) => WorkshopPartLayer.getPinIcon[`${data.LayerContext}_xs`],
                        iconSize: [40, 51],
                        iconAnchor: [20, 48],
                        labelAnchor: [25, -32]
                    },
                    riseOnHover: true
                },
            },
            noAllowClusterHull: true,
        });
    }

    static getPinIcon = {
        [LayerContextEnum.EletricStation]: `${environment.get('IMAGES_HOST')}/pin_eletric_station.png`,
        [`${LayerContextEnum.EletricStation}_sm`]: `${environment.get('IMAGES_HOST')}/pin_circle_green.png`,
        [`${LayerContextEnum.EletricStation}_xs`]: `${environment.get('IMAGES_HOST')}/pin_circle_green_xs.png`,
        [LayerContextEnum.FuelStation]: `${environment.get('IMAGES_HOST')}/pin_fuel_station.png`,
        [`${LayerContextEnum.FuelStation}_sm`]: `${environment.get('IMAGES_HOST')}/pin_circle_pink.png`,
        [`${LayerContextEnum.FuelStation}_xs`]: `${environment.get('IMAGES_HOST')}/pin_circle_pink_xs.png`,
    };

    static getLegendName = {
        [LayerContextEnum.EletricStation]: () => translate('Common.EletricStationLegend'),
        [LayerContextEnum.FuelStation]: () => translate('Common.FuelStationLegend'),
    }
}

export default FuelStationLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/FuelStationLayer.js